import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setAuthError } from '../lib/queryClient';

/**
 * Hook for background session validation on web platform
 * Validates session every 30 seconds when component is mounted
 *
 * Phase 2.1: Enhanced validation for proactive session checking
 */
export const useAuthValidation = () => {
  const { validateSession, isAuthenticated } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isValidatingRef = useRef(false);

  useEffect(() => {
    // Only run on web platform and when authenticated
    if (typeof window === 'undefined' || !isAuthenticated) {
      return;
    }

    const validateInBackground = async () => {
      // Prevent concurrent validations
      if (isValidatingRef.current) {
        return;
      }

      try {
        isValidatingRef.current = true;

        const isValid = await validateSession();

        if (isValid) {
          // Reset auth error flag on successful validation
          setAuthError(false);
        }
        // Auth error flag will be set by AuthContext's validateSession if invalid
      } catch (error) {
        console.error('❌ useAuthValidation: Validation error:', error);
        // Don't set auth error flag for network errors - could be offline
      } finally {
        isValidatingRef.current = false;
      }
    };

    // Initial validation
    validateInBackground();

    // Set up interval for periodic validation (every 30 seconds)
    intervalRef.current = setInterval(validateInBackground, 30000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isValidatingRef.current = false;
    };
  }, [isAuthenticated, validateSession]);

  // No return value needed - this is a side-effect hook
};
